// The `custom_remote_support` DM post (shared/REMOTE-SUPPORT.md §2): the plugin writes it when a session is
// requested and UPDATES it on every state change, so history shows the session. Satori clients draw it as a
// compact system-style row (icon + text); while it is live and the reader is the helper, the row carries
// «Unirse» (accept a pending request / re-open the viewer). The text is ours (both languages) and falls back
// to the plugin's Spanish message only when the props are unreadable.
import { t, tf } from '../i18n/i18n';

export const REMOTE_SUPPORT_POST_TYPE = 'custom_remote_support';

export interface RemoteSupportPostProps {
  sessionId: string;
  requesterId: string;
  helperId: string;
  state: string;
  startedAt: number;
  endedAt: number;
}

export function isRemoteSupportPost(type: string | null | undefined): boolean {
  return type === REMOTE_SUPPORT_POST_TYPE;
}

export function remoteSupportPostProps(propsJson: string | null | undefined): RemoteSupportPostProps | null {
  if (!propsJson) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(propsJson);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const s = (v: unknown) => (typeof v === 'string' ? v : '');
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : 0);
  const sessionId = s(p.session_id);
  if (!sessionId) return null;
  return { sessionId, requesterId: s(p.requester_id), helperId: s(p.helper_id), state: s(p.state), startedAt: n(p.started_at), endedAt: n(p.ended_at) };
}

/** «12 min», «1 h 5 min», «menos de 1 min». */
export function remoteSupportDuration(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return t('web.rs.duration.lessThanMinute');
  if (minutes < 60) return tf('web.rs.duration.minutes', minutes);
  return tf('web.rs.duration.hours', Math.floor(minutes / 60), minutes % 60);
}

export interface RemoteSupportPostRow {
  text: string;
  /** «Unirse»: the reader is the helper and the session is still requested / connecting / active. */
  joinable: boolean;
  live: boolean;
  sessionId: string;
}

/**
 * What the row says. `nameOf` resolves a user id to a display name; `me` is the reader. The OTHER person is
 * named (the requester reads «Soporte remoto con Ana», the helper «… con Alex»); a pending request names the
 * requester («Alex solicitó soporte remoto.»).
 */
export function remoteSupportPostRow(props: RemoteSupportPostProps | null, fallback: string, me: string, nameOf: (id: string) => string): RemoteSupportPostRow {
  if (!props) return { text: fallback || t('web.rs.title'), joinable: false, live: false, sessionId: '' };
  const other = me === props.requesterId ? props.helperId : props.requesterId;
  const otherName = nameOf(other) || '?';
  const helperIsMe = me !== '' && me === props.helperId;
  switch (props.state) {
    case 'requested':
      return { text: tf('web.rs.post.requested', nameOf(props.requesterId) || '?'), joinable: helperIsMe, live: true, sessionId: props.sessionId };
    case 'connecting':
    case 'active':
      return { text: tf('web.rs.post.live', otherName), joinable: helperIsMe, live: true, sessionId: props.sessionId };
    case 'ended':
      return {
        text: props.startedAt && props.endedAt > props.startedAt ? tf('web.rs.post.ended', otherName, remoteSupportDuration(props.endedAt - props.startedAt)) : tf('web.rs.post.endedShort', otherName),
        joinable: false,
        live: false,
        sessionId: props.sessionId,
      };
    case 'declined':
      return { text: t('web.rs.post.declined'), joinable: false, live: false, sessionId: props.sessionId };
    case 'cancelled':
      return { text: t('web.rs.post.cancelled'), joinable: false, live: false, sessionId: props.sessionId };
    case 'expired':
      return { text: t('web.rs.post.expired'), joinable: false, live: false, sessionId: props.sessionId };
    default:
      return { text: fallback || t('web.rs.title'), joinable: false, live: false, sessionId: props.sessionId };
  }
}
